/**
 * PageActionRenderer — handles `page_action` content items.
 *
 * Always auto-fires (one-shot per render via a useRef guard). The verb is
 * proxied through parentBridge.dispatchAction → bootstrap's wg:action
 * handler, which runs against the partner host page DOM.
 *
 * History re-renders never re-fire — replaying old highlights/scrolls
 * would yank the user around for no reason.
 *
 * On dispatch failure (mutating verb without consent, element gone,
 * timeout) we surface nothing to the user — the bootstrap handles its
 * own logging. The optional `rationale` is shown either way so the user
 * sees that the agent intended a side effect even if it failed silently.
 */
import React, { useEffect, useRef, useState } from 'react';
import type { PageActionContentItem } from '@wg/types';
import type { Renderer } from '../registry';
import { executePageAction } from '../actions';
import logger from '../../utils/logger';

const PageActionRenderer: Renderer = ({ item, ctx }) => {
  const a = item as PageActionContentItem;
  const fired = useRef(false);
  const [outcome, setOutcome] = useState<'pending' | 'ok' | 'failed'>(
    'pending',
  );

  useEffect(() => {
    if (ctx.isHistory) return;
    if (fired.current) return;
    if (!ctx.actionDeps) {
      logger.warn('[PageActionRenderer] auto-fire skipped — no actionDeps');
      return;
    }
    fired.current = true;
    executePageAction(a.verb, a.args, ctx.actionDeps).then(
      result => setOutcome(result.ok ? 'ok' : 'failed'),
      () => setOutcome('failed'),
    );
  }, [a, ctx]);

  if (!a.rationale) return null;

  // Lightweight inline note. We do NOT render the verb name — partners
  // shouldn't see internal verb labels in the chat thread.
  return (
    <div
      className="wg-chat__page-action"
      data-outcome={outcome}
      role="status"
      aria-live="polite"
    >
      <span className="wg-chat__page-action-icon" aria-hidden>
        {outcome === 'failed' ? '⚠' : '✦'}
      </span>
      <span className="wg-chat__page-action-rationale">{a.rationale}</span>
    </div>
  );
};

export default PageActionRenderer;
